import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('officiant');
export interface OfficiantRec {
  id: string;
  userId: string;
  officiantName?: string;
  phoneNumber?: string;
  email?: string;
  choice?: string;
  notes?: string;
  isLocked?: boolean;
}

class OfficiantService {
  async getAllOfficiants(): Promise<OfficiantRec[]> {
    const response = await http.get('/api/officiant');

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch officiant information');
    }

    return response.data.data || [];
  }

  async getOfficiantById(id: string): Promise<OfficiantRec | null> {
    try {
      const response = await http.get(`/api/officiant/${id}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch officiant information');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching officiant by ID:', error);
      return null;
    }
  }

  async createOfficiant(officiantData: Partial<OfficiantRec>): Promise<OfficiantRec> {
    const response = await http.post('/api/officiant', officiantData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save officiant information');
    }

    return response.data.data;
  }

  async updateOfficiant(id: string, officiantData: Partial<OfficiantRec>): Promise<OfficiantRec> {
    const response = await http.patch(`/api/officiant/${id}`, officiantData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update officiant information');
    }

    return response.data.data;
  }

  async deleteOfficiant(id: string): Promise<void> {
    const response = await http.delete(`/api/officiant/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete officiant information');
    }
  }

  async lockOfficiant(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for officiant ID:', id);
    const response = await http.post(`/api/officiant/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock officiant information');
    }
  }

  async unlockOfficiant(id: string, granteeEmails: string[]): Promise<void> {
    logger.debug('[Service Debug] Calling unlock API for officiant ID:', id, 'granteeEmails:', granteeEmails);
    const response = await http.post(`/api/officiant/${id}/unlock`, { granteeEmails });
    logger.debug('[Service Debug] Unlock API response:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock officiant information');
    }
  }

  async getOfficiantAccessGrants(id: string): Promise<any[]> {
    try {
      const response = await http.get(`/api/officiant/${id}/access-grants`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch officiant access grants');
      }

      return response.data.data || [];
    } catch (error) {
      logger.error('Error fetching officiant access grants:', error);
      return [];
    }
  }

  async deleteOfficiantAccessGrant(id: string, grantId: string): Promise<void> {
    const response = await http.delete(`/api/officiant/${id}/access-grants/${grantId}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete access grant');
    }
  }
}

export const officiantService = new OfficiantService();